
import { useState, useEffect, useRef, RefObject } from 'react';

interface IntersectionObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}

/**
 * Hook personnalisé pour observer quand un élément entre dans le viewport
 * Utilise l'API Intersection Observer
 */
export const useIntersectionObserver = <T extends Element>(
  options: IntersectionObserverOptions = {},
  initialVisible = false
): [RefObject<T>, boolean] => {
  const [isVisible, setIsVisible] = useState<boolean>(initialVisible);
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, {
      root: options.root || null,
      rootMargin: options.rootMargin || '0px',
      threshold: options.threshold || 0.1,
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [options.root, options.rootMargin, options.threshold]);

  return [ref, isVisible];
};

export default useIntersectionObserver;
